import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * C26 Hub Access — the "how it works" and "ground rules" section for the
 * Residences at Bluhawk × C26 Hub partnership. Three stacked panels:
 *
 *   1. Registration Process — five numbered onboarding steps.
 *   2. Pool Rules — strictly-enforced water & reservation policies.
 *   3. General Facility Requirements — compliance expectations.
 *
 * All copy is editable via richtext tokens so the on-site team can tweak
 * cadence, phone numbers, or policy wording without a code change.
 */

export function C26HubAccess() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up c26-access">
      <div className="c26-access__container">

        {/* ── Registration Process ── */}
        <article className="c26-access__panel">
          <header className="c26-access__panel-header">
            <RichTextContent
              path="c26hubaccess.register_eyebrow"
              label="Registration Eyebrow"
              defaultValue="<p>Getting Started</p>"
              as="p"
              className="c26-access__eyebrow"
            />
            <RichTextContent
              path="c26hubaccess.register_heading"
              label="Registration Heading"
              defaultValue="<p>Registration Process</p>"
              as="h2"
              className="c26-access__heading"
            />
            <RichTextContent
              path="c26hubaccess.register_intro"
              label="Registration Intro"
              defaultValue="<p>Five quick steps and you&rsquo;re booking classes from your phone.</p>"
              as="div"
              className="c26-access__body"
            />
          </header>

          <ol className="c26-access__steps">
            <li className="c26-access__step">
              <span className="c26-access__step-num" aria-hidden="true">1</span>
              <div>
                <RichTextContent
                  path="c26hubaccess.step1_title"
                  label="Step 1 Title"
                  defaultValue="<p>Download the Apps</p>"
                  as="h3"
                  className="c26-access__step-title"
                />
                <RichTextContent
                  path="c26hubaccess.step1_body"
                  label="Step 1 Description"
                  defaultValue="<p>Install the <strong>Momence</strong> and <strong>UniFi</strong> apps on your phone before you visit C26 Hub.</p>"
                  as="div"
                  className="c26-access__step-text"
                />
              </div>
            </li>

            <li className="c26-access__step">
              <span className="c26-access__step-num" aria-hidden="true">2</span>
              <div>
                <RichTextContent
                  path="c26hubaccess.step2_title"
                  label="Step 2 Title"
                  defaultValue="<p>Visit C26 In Person</p>"
                  as="h3"
                  className="c26-access__step-title"
                />
                <RichTextContent
                  path="c26hubaccess.step2_body"
                  label="Step 2 Description"
                  defaultValue="<p>Stop by C26 Hub during normal business hours to complete your in-person check-in.</p>"
                  as="div"
                  className="c26-access__step-text"
                />
              </div>
            </li>

            <li className="c26-access__step">
              <span className="c26-access__step-num" aria-hidden="true">3</span>
              <div>
                <RichTextContent
                  path="c26hubaccess.step3_title"
                  label="Step 3 Title"
                  defaultValue="<p>Present Proof of Residency</p>"
                  as="h3"
                  className="c26-access__step-title"
                />
                <RichTextContent
                  path="c26hubaccess.step3_body"
                  label="Step 3 Description"
                  defaultValue="<p>Bring a government-issued ID and your <strong>Hawkflock card</strong> so the team can verify your resident status.</p>"
                  as="div"
                  className="c26-access__step-text"
                />
              </div>
            </li>

            <li className="c26-access__step">
              <span className="c26-access__step-num" aria-hidden="true">4</span>
              <div>
                <RichTextContent
                  path="c26hubaccess.step4_title"
                  label="Step 4 Title"
                  defaultValue="<p>Complete the Onboarding Walkthrough</p>"
                  as="h3"
                  className="c26-access__step-title"
                />
                <RichTextContent
                  path="c26hubaccess.step4_body"
                  label="Step 4 Description"
                  defaultValue="<p>A C26 team member will walk you through the facility, equipment, and policies you&rsquo;ll need to know.</p>"
                  as="div"
                  className="c26-access__step-text"
                />
              </div>
            </li>

            <li className="c26-access__step">
              <span className="c26-access__step-num" aria-hidden="true">5</span>
              <div>
                <RichTextContent
                  path="c26hubaccess.step5_title"
                  label="Step 5 Title"
                  defaultValue="<p>Reserve From Your Phone</p>"
                  as="h3"
                  className="c26-access__step-title"
                />
                <RichTextContent
                  path="c26hubaccess.step5_body"
                  label="Step 5 Description"
                  defaultValue="<p>From that point on, use the Momence and UniFi apps to schedule every gym, pool, and class reservation.</p>"
                  as="div"
                  className="c26-access__step-text"
                />
              </div>
            </li>
          </ol>
        </article>

        {/* ── Pool Rules + Facility Requirements (side-by-side on desktop) ── */}
        <div className="c26-access__rules-row">

          {/* Pool Rules */}
          <article className="c26-access__panel c26-access__panel--alert">
            <header className="c26-access__panel-header c26-access__panel-header--compact">
              <span className="c26-access__alert-badge" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M10.3 3.7a2 2 0 0 1 3.4 0l8 14a2 2 0 0 1-1.7 3H4.3a2 2 0 0 1-1.7-3l8-14z" />
                </svg>
              </span>
              <div>
                <RichTextContent
                  path="c26hubaccess.pool_eyebrow"
                  label="Pool Rules Eyebrow"
                  defaultValue="<p>Strictly Enforced</p>"
                  as="p"
                  className="c26-access__eyebrow c26-access__eyebrow--alert"
                />
                <RichTextContent
                  path="c26hubaccess.pool_heading"
                  label="Pool Rules Heading"
                  defaultValue="<p>Pool Rules</p>"
                  as="h2"
                  className="c26-access__heading"
                />
              </div>
            </header>
            <RichTextContent
              path="c26hubaccess.pool_rules"
              label="Pool Rules List"
              defaultValue={
                '<ul>' +
                  '<li>Minimum age requirement: <strong>13 years old</strong></li>' +
                  '<li><strong>Reservation required</strong> &mdash; no walk-ins permitted</li>' +
                  '<li>Users must <strong>remain in the water</strong></li>' +
                  '<li>Pool toys are <strong>not permitted</strong></li>' +
                  '<li>Jumping or diving is <strong>not allowed</strong></li>' +
                '</ul>'
              }
              as="div"
              className="c26-access__rule-list c26-access__rule-list--alert"
            />
          </article>

          {/* Facility Requirements */}
          <article className="c26-access__panel">
            <header className="c26-access__panel-header c26-access__panel-header--compact">
              <span className="c26-access__info-badge" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5" />
                  <path d="M12 17h.01" />
                </svg>
              </span>
              <div>
                <RichTextContent
                  path="c26hubaccess.facility_eyebrow"
                  label="Facility Eyebrow"
                  defaultValue="<p>Please Note</p>"
                  as="p"
                  className="c26-access__eyebrow"
                />
                <RichTextContent
                  path="c26hubaccess.facility_heading"
                  label="Facility Heading"
                  defaultValue="<p>General Facility Requirements</p>"
                  as="h2"
                  className="c26-access__heading"
                />
              </div>
            </header>
            <RichTextContent
              path="c26hubaccess.facility_rules"
              label="Facility Requirements List"
              defaultValue={
                '<ul>' +
                  '<li>Gym access is <strong>reservation-only</strong> (5:00 AM &ndash; 2:00 PM)</li>' +
                  '<li>Free guest access is <strong>not permitted</strong></li>' +
                  '<li>Residents must comply with all C26 policies and safety procedures</li>' +
                '</ul>'
              }
              as="div"
              className="c26-access__rule-list"
            />
          </article>
        </div>
      </div>

      <style>{`
        .c26-access {
          background: var(--vibe-color-background-alt, #f6f6f8);
          padding: 3.5rem 0 4rem;
        }
        @media (min-width: 768px) {
          .c26-access { padding: 5rem 0 5.5rem; }
        }

        .c26-access__container {
          max-width: 76rem;
          margin: 0 auto;
          padding: 0 1rem;
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        @media (min-width: 640px) { .c26-access__container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .c26-access__container { padding: 0 2rem; gap: 2.5rem; } }

        .c26-access__panel {
          background: #ffffff;
          border-radius: var(--vibe-radius-lg, 14px);
          box-shadow: 0 10px 32px rgba(0, 0, 0, 0.06);
          padding: 1.75rem 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        @media (min-width: 768px) {
          .c26-access__panel { padding: 2.5rem 2.5rem 2.75rem; gap: 1.75rem; }
        }

        .c26-access__panel--alert {
          border-left: 4px solid var(--vibe-color-secondary);
        }

        .c26-access__panel-header {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .c26-access__panel-header--compact {
          flex-direction: row;
          align-items: center;
          gap: 0.9rem;
        }
        .c26-access__panel-header--compact > div {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }

        .c26-access__eyebrow {
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.3em;
          color: var(--vibe-color-secondary);
          margin: 0;
          font-weight: 600;
        }
        .c26-access__eyebrow--alert {
          color: var(--vibe-color-secondary);
        }

        .c26-access__heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.6vw, 2rem);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          line-height: 1.1;
          color: var(--vibe-color-text);
          margin: 0.3rem 0 0;
        }

        .c26-access__body {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }

        .c26-access__alert-badge,
        .c26-access__info-badge {
          flex-shrink: 0;
          width: 2.4rem;
          height: 2.4rem;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #ffffff);
        }

        /* ── Numbered steps ── */
        .c26-access__steps {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .c26-access__steps { grid-template-columns: 1fr 1fr; gap: 1.25rem 2rem; }
        }

        .c26-access__step {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
          padding: 0.75rem 0.75rem 0.75rem 0.5rem;
          border-radius: var(--vibe-radius-md, 10px);
          background: var(--vibe-color-background-alt, #f6f6f8);
        }

        .c26-access__step-num {
          flex-shrink: 0;
          width: 2.1rem;
          height: 2.1rem;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #ffffff);
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0;
        }

        .c26-access__step-title {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 700;
          color: var(--vibe-color-text);
          margin: 0 0 0.3rem;
          line-height: 1.25;
        }

        .c26-access__step-text {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.55;
          color: var(--vibe-color-text-muted);
        }
        .c26-access__step-text a {
          color: var(--vibe-color-secondary);
          text-decoration: none;
        }
        .c26-access__step-text a:hover {
          text-decoration: underline;
        }
        .c26-access__step-text strong {
          color: var(--vibe-color-text);
          font-weight: 600;
        }

        /* ── Rules row ── */
        .c26-access__rules-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 900px) {
          .c26-access__rules-row { grid-template-columns: 1fr 1fr; gap: 2.5rem; }
        }

        .c26-access__rule-list ul {
          margin: 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }

        .c26-access__rule-list li {
          position: relative;
          padding-left: 1.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
        }
        .c26-access__rule-list li strong {
          font-weight: 700;
          color: var(--vibe-color-text);
        }

        .c26-access__rule-list li::before {
          content: '';
          position: absolute;
          left: 0.25rem;
          top: 0.55rem;
          width: 0.6rem;
          height: 0.6rem;
          border-radius: 50%;
          background: var(--vibe-color-secondary);
        }

        .c26-access__rule-list--alert li::before {
          background: var(--vibe-color-secondary);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--vibe-color-secondary) 18%, transparent);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'C26HubAccess',
  displayName: 'C26 Hub — Registration & Rules',
  tokens: {
    // Registration process
    'c26hubaccess.register_eyebrow': {
      label: 'Registration Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Getting Started</p>',
    },
    'c26hubaccess.register_heading': {
      label: 'Registration Heading',
      type: 'richtext',
      defaultValue: '<p>Registration Process</p>',
    },
    'c26hubaccess.register_intro': {
      label: 'Registration Intro',
      type: 'richtext',
      defaultValue: '<p>Five quick steps and you&rsquo;re booking classes from your phone.</p>',
    },
    'c26hubaccess.step1_title': {
      label: 'Step 1 Title',
      type: 'richtext',
      defaultValue: '<p>Download the Apps</p>',
    },
    'c26hubaccess.step1_body': {
      label: 'Step 1 Description',
      type: 'richtext',
      defaultValue:
        '<p>Install the <strong>Momence</strong> and <strong>UniFi</strong> apps on your phone before you visit C26 Hub.</p>',
    },
    'c26hubaccess.step2_title': {
      label: 'Step 2 Title',
      type: 'richtext',
      defaultValue: '<p>Visit C26 In Person</p>',
    },
    'c26hubaccess.step2_body': {
      label: 'Step 2 Description',
      type: 'richtext',
      defaultValue: '<p>Stop by C26 Hub during normal business hours to complete your in-person check-in.</p>',
    },
    'c26hubaccess.step3_title': {
      label: 'Step 3 Title',
      type: 'richtext',
      defaultValue: '<p>Present Proof of Residency</p>',
    },
    'c26hubaccess.step3_body': {
      label: 'Step 3 Description',
      type: 'richtext',
      defaultValue:
        '<p>Bring a government-issued ID and your <strong>Hawkflock card</strong> so the team can verify your resident status.</p>',
    },
    'c26hubaccess.step4_title': {
      label: 'Step 4 Title',
      type: 'richtext',
      defaultValue: '<p>Complete the Onboarding Walkthrough</p>',
    },
    'c26hubaccess.step4_body': {
      label: 'Step 4 Description',
      type: 'richtext',
      defaultValue:
        '<p>A C26 team member will walk you through the facility, equipment, and policies you&rsquo;ll need to know.</p>',
    },
    'c26hubaccess.step5_title': {
      label: 'Step 5 Title',
      type: 'richtext',
      defaultValue: '<p>Reserve From Your Phone</p>',
    },
    'c26hubaccess.step5_body': {
      label: 'Step 5 Description',
      type: 'richtext',
      defaultValue:
        '<p>From that point on, use the Momence and UniFi apps to schedule every gym, pool, and class reservation.</p>',
    },

    // Pool rules
    'c26hubaccess.pool_eyebrow': {
      label: 'Pool Rules Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Strictly Enforced</p>',
    },
    'c26hubaccess.pool_heading': {
      label: 'Pool Rules Heading',
      type: 'richtext',
      defaultValue: '<p>Pool Rules</p>',
    },
    'c26hubaccess.pool_rules': {
      label: 'Pool Rules List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Minimum age requirement: <strong>13 years old</strong></li><li><strong>Reservation required</strong> &mdash; no walk-ins permitted</li><li>Users must <strong>remain in the water</strong></li><li>Pool toys are <strong>not permitted</strong></li><li>Jumping or diving is <strong>not allowed</strong></li></ul>',
    },

    // Facility requirements
    'c26hubaccess.facility_eyebrow': {
      label: 'Facility Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Please Note</p>',
    },
    'c26hubaccess.facility_heading': {
      label: 'Facility Heading',
      type: 'richtext',
      defaultValue: '<p>General Facility Requirements</p>',
    },
    'c26hubaccess.facility_rules': {
      label: 'Facility Requirements List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Gym access is <strong>reservation-only</strong> (5:00 AM &ndash; 2:00 PM)</li><li>Free guest access is <strong>not permitted</strong></li><li>Residents must comply with all C26 policies and safety procedures</li></ul>',
    },
  },
};

(C26HubAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default C26HubAccess;
